import { useEffect, useMemo, useRef, useState } from "react";
import { fetchJson } from "../api";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Switch,
  Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton, TextField,
  Card, CardContent, Alert, FormControl, Select, MenuItem, CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockIcon from "@mui/icons-material/Lock";
import { Button } from "@mui/material";
import TimelineBars from "../components/postplan/TimelineBars";
// ===== AI Assistant helpers (UI only) =====
const fmtDateIT = (s) => {
  if (!s) return "—";
  const ymd = String(s).slice(0, 10); // YYYY-MM-DD
  const [y, m, d] = ymd.split("-");
  if (!y || !m || !d) return ymd;
  return `${d}-${m}-${y}`; // DD-MM-YYYY
};

// Alias per evitare no-undef se nel file stai usando ancora fmtDate / fmtDMY
const fmtDate = fmtDateIT;
const fmtDMY = fmtDateIT;

const safeNum = (n) => {
  const x = Number(n);
  return Number.isFinite(x) ? x : null;
};

const normalizeSpaces = (t) => String(t || "").replace(/\s+/g, " ").trim();

const translateNoteIT = (note) => {
  let s = normalizeSpaces(note);
  if (!s) return "";
  return s
    .replace(/soft overload/gi, "Linea satura")
    .replace(/\butil\b/gi, "carico")
    .replace(/\bpeak\b/gi, "picco")
    .replace(/\bcap\b/gi, "capienza")
    .replace(/\bwork\b/gi, "giorni");
};

// Se ti serve trovare il nome linea da layout.lanes
const findLineLabelById = (data, lineId) => {
  const id = String(lineId ?? "");
  const lanes = data?.layout?.lanes;
  if (!id || !Array.isArray(lanes)) return null;

  const lane =
    lanes.find((x) => String(x?.id) === id) ||
    lanes.find((x) => String(x?.line_id) === id) ||
    lanes.find((x) => String(x?.lineId) === id);

  const lbl =
    lane?.label ||
    lane?.lineLabel ||
    lane?.name ||
    lane?.title ||
    lane?.assignee_label ||
    lane?.assigneeLabel ||
    null;

  return lbl && String(lbl).trim() ? String(lbl).trim() : null;
};

// Riassunto overload (se lo usi in alto)
const summarizeOverload = (w) => {
  const peak = safeNum(w?.peak);
  const cap = safeNum(w?.capacity);
  const util = safeNum(w?.utilization_pct ?? w?.utilizationPct ?? w?.util);
  const work = w?.work ? String(w.work) : null;

  const parts = [];

  if (peak != null && cap != null) {
    if (peak > cap) parts.push(`Sovrapposizione: ${peak} attività / capienza ${cap}`);
    else parts.push(`Nessuna sovrapposizione (picco ${peak} ≤ capienza ${cap})`);
  }

  if (util != null) parts.push(`Carico: ${util}%`);
  if (work) parts.push(`Giorni: ${work}`);

  return parts.join(" • ");
};

const translateNote = (note) => {
  const s = String(note || "").trim();
  if (!s) return "";
  return s
    .replace(/soft overload/gi, "Linea satura")
    .replace(/util/gi, "copertura")
    .replace(/peak/gi, "picco")
    .replace(/cap/gi, "capienza")
    .replace(/work/gi, "giorni");
};
export default function PostPlanTimelinePage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [lineDraft, setLineDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [planNameDraft, setPlanNameDraft] = useState("");
  // ---- AI Assistant UI state ----
const [aiMode, setAiMode] = useState("overload");
const [aiLoading, setAiLoading] = useState(false);
const [aiError, setAiError] = useState("");
const [aiResult, setAiResult] = useState(null);
const [aiElapsed, setAiElapsed] = useState(0);

useEffect(() => {
  if (!aiLoading) {
    setAiElapsed(0);
    return;
  }
  const t = setInterval(() => setAiElapsed((s) => s + 1), 1000);
  return () => clearInterval(t);
}, [aiLoading]);
const runAI = async () => {
  setAiError("");
  setAiLoading(true);

  // timeout lungo per Ollama
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 180000); // 180s

  try {
    const payload = {
      planId: Number(id),
      mode: aiMode,
      question: "",
    };

    const res = await fetch("/api/ai/postplan/assistant", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    let data = null;
    try {
      data = await res.json();
    } catch {
      /* ignore */
    }

    if (!res.ok) {
      throw new Error(data?.detail || data?.error || `HTTP ${res.status}`);
    }

    setAiResult(data);
  } catch (e) {
    console.error("[AI UI] error:", e);
    setAiResult(null);

    if (e.name === "AbortError") {
      setAiError("Timeout AI (richiesta troppo lunga)");
    } else {
      setAiError(e?.message || "Errore AI");
    }
  } finally {
    clearTimeout(timeoutId);
    setAiLoading(false);
  }
};
  // ---- Episodes UI state (collapsible / completed) ----
const storageKey = `postplan:${id}:completedEpisodes`;

const [completedEpisodes, setCompletedEpisodes] = useState(() => {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
});

const [showCompleted, setShowCompleted] = useState(false);
const [applyToWholePlan, setApplyToWholePlan] = useState(true);
const [batchRename, setBatchRename] = useState(true);
const [expandedEp, setExpandedEp] = useState(null);
const didAutoOpenRef = useRef(false);
useEffect(() => {
  try {
    localStorage.setItem(
      storageKey,
      JSON.stringify(Array.from(completedEpisodes))
    );
  } catch {}
}, [completedEpisodes, storageKey]);

const isCompleted = (epId) => completedEpisodes.has(epId);

const toggleCompleted = (epId) => {
  setCompletedEpisodes((prev) => {
    const next = new Set(prev);
    if (next.has(epId)) next.delete(epId);
    else next.add(epId);
    return next;
  });
};


  useEffect(() => {
  let alive = true;

  setLoading(true);
  fetchJson(`/postplans/${id}/timeline`)
    .then((d) => {
      if (!alive) return;
      setData(d);
      setPlanNameDraft(d?.plan?.name || "");
      console.log("TIMELINE sample line", d?.episodes?.[0]?.phases?.[0]?.lines?.[0]);
      setLoading(false);
    })
    .catch((e) => {
      console.error(e);
      if (!alive) return;
      setData(null);
      setLoading(false);
    });

  return () => {
    alive = false;
  };
}, [id]);
const patchPlanName = async (newName) => {
  setApiErr("");
  try {
    const name = String(newName || "").trim();
    if (!name) return;

    await fetchJson(`/postplans/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });

    await refreshTimeline();
  } catch (e) {
    console.error(e);
    setApiErr(e?.message || "Errore salvataggio nome piano");
  }
};
const refreshTimeline = async () => {
  setApiErr("");
  try {
    const d = await fetchJson(`/postplans/${id}/timeline?t=${Date.now()}`);
    setData(d);
  } catch (e) {
    console.error(e);
    setApiErr(e?.message || "Errore refresh timeline");
  }
};
const patchBlockOverride = async (blockId, allowSat, allowSun) => {
  setApiErr("");
  try {
    await fetchJson(`/postplans/blocks/${blockId}/override`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ allowSat, allowSun }),
    });

    // refresh dati (robusto)
    await refreshTimeline();
  } catch (e) {
    console.error(e);
    setApiErr(e?.message || "Errore salvataggio weekend override");
  }
};
// --- Line labels (batch) ---
const patchLineLabelBatch = async (planId, phaseId, lineNo, label) => {
  setApiErr("");
  try {
    if (!planId || !phaseId || !lineNo) return;

    await fetchJson(`/postplans/${planId}/lines/label`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phaseId, lineNo, label }),
    });

    await refreshTimeline();
  } catch (e) {
    console.error(e);
    setApiErr(e?.message || "Errore salvataggio nome linea");
  }
};

// --- Line label (single) ---
const patchLineLabel = async (planId, lineId, label) => {
  setApiErr("");
  try {
    console.log("PATCH LINE LABEL ->", { planId, lineId, label });

    const res = await fetchJson(`/postplans/${planId}/lines/${lineId}/label`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ label }),
    });

    console.log("PATCH LINE LABEL OK <-", res);

    await refreshTimeline();
  } catch (e) {
    console.error("PATCH LINE LABEL FAIL", e);
    setApiErr(e?.message || "Errore salvataggio nome linea");
  }
};

const patchBlockNote = async (blockId, partLabel) => {
  setApiErr("");
  try {
    await fetchJson(`/postplans/blocks/${blockId}/note`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ partLabel }),
    });

    // refresh dati (robusto)
    await refreshTimeline();
  } catch (e) {
    console.error(e);
    setApiErr(e?.message || "Errore salvataggio parte puntata");
  }
};


const [apiErr, setApiErr] = useState("");
const navigate = useNavigate();

// Auto-open: apre il PRIMO episodio non completato (una sola volta)
useEffect(() => {
  if (didAutoOpenRef.current) return;

  const eps = data?.episodes;
  if (!Array.isArray(eps) || eps.length === 0) return;

  const firstActive = eps.find((ep) => !isCompleted(ep.id));
  if (firstActive) {
    setExpandedEp(firstActive.id);
    didAutoOpenRef.current = true;
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [data]);

// ✅ Hook SEMPRE prima dei return anticipati
const lockByBlockId = useMemo(() => {
  const m = new Map();
  const flat = data?.blocksFlat;
  if (!Array.isArray(flat)) return m;
  for (const x of flat) m.set(x.id, !!x.isLocked);
  return m;
}, [data]);

// ===== AI Assistant helpers (UI only) =====
const fmtDMY = (s) => {
  if (!s) return "—";
  const [y, m, d] = String(s).slice(0, 10).split("-");
  return `${d}-${m}-${y}`;
};

const normalizeSpaces = (t) => String(t || "").replace(/\s+/g, " ").trim();

const translateOverloadNote = (note) => {
  let t = normalizeSpaces(note);
  if (!t) return "";

  t = t.replace(/Soft overload/gi, "Linea satura");
  t = t.replace(/\bwork\b/gi, "giorni");
  t = t.replace(/\butil\b/gi, "carico");
  t = t.replace(/peak\s*([0-9?]+)\s*\/\s*cap\s*([0-9?]+)/gi, "picco $1 / capienza $2");
  t = t.replace(/\(\s*carico\s*([0-9]+)%\s*\)/gi, "(carico $1%)");

  return t;
};

// ✅ UNA SOLA mappa per il nome linea (NO useMemo dopo i return)
const lineNameById = useMemo(() => {
  const m = new Map();

  // 1) layout.lanes
  const lanes = Array.isArray(data?.layout?.lanes) ? data.layout.lanes : [];
  for (const ln of lanes) {
    const k = ln?.id ?? ln?.lineId ?? ln?.line_id;
    const name = (ln?.label || ln?.name || ln?.title || ln?.lineLabel || "").trim();
    if (k != null && name) m.set(String(k), name);
  }

  // 2) episodes -> phases -> lines (qui spesso c'è lineLabel)
  // 2) episodes -> phases -> lines (qui spesso c'è lineLabel)
const eps = Array.isArray(data?.episodes) ? data.episodes : [];
for (const ep of eps) {
  const phases = Array.isArray(ep?.phases) ? ep.phases : [];
  for (const ph of phases) {
    const lines = Array.isArray(ph?.lines) ? ph.lines : [];
    for (const ln of lines) {
      const name = (ln?.lineLabel || ln?.label || ln?.name || "").trim();
      if (!name) continue;

      const k1 = ln?.lineId ?? ln?.id;
      const k2 = ln?.line_id;
      const k3 = ln?.lineNo; // <<< fallback fondamentale

      if (k1 != null && !m.has(String(k1))) m.set(String(k1), name);
      if (k2 != null && !m.has(String(k2))) m.set(String(k2), name);
      if (k3 != null && !m.has(String(k3))) m.set(String(k3), name);
    }
  }
}

  return m;
}, [data]);

const prettyLineLabel = (lineId, fallback) => {
  const id = lineId != null ? String(lineId) : "";
  const fromMap = id ? lineNameById.get(id) : null;
  if (fromMap && fromMap.trim()) return fromMap.trim();

  const cleanFallback = (fallback ? String(fallback) : "").trim();
  return cleanFallback || (id ? `Linea ${id}` : "Linea");
};

// ✅ SOLO DOPO: return anticipato (nessun hook sotto!)
if (!data || !data.plan || !Array.isArray(data.episodes)) {
  return (
    <Box p={3}>
      <Typography variant="h6">Nessun piano disponibile</Typography>
      <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
        Non esiste ancora nessun piano. Crea un piano demo per vedere la timeline.
      </Typography>

      <Button
        variant="contained"
        onClick={async () => {
          try {
            const r = await fetchJson("/postplans/demo", { method: "POST" });
            if (r?.planId) window.location.href = `/postplans/${r.planId}`;
            else alert("Demo creata ma risposta inattesa");
          } catch (e) {
            console.error(e);
            alert(e?.message || "Errore creazione demo");
          }
        }}
      >
        Crea piano demo
      </Button>
    </Box>
  );
}

// ✅ QUI: ora data è valida, quindi posso estrarre
const { plan, episodes, layout, blocksFlat } = data;

const getLineTitle = (w, idx) => {
  const rawId =
    w?.line_id ?? w?.lineId ?? w?.lineNo ?? w?.line_no ?? null;

  const key = rawId != null ? String(rawId) : "";
  const fromMap = key ? lineNameById.get(key) : null;

  return (fromMap && fromMap.trim())
    ? fromMap.trim()
    : (w?.label || (key ? `Linea ${key}` : `Linea ${idx + 1}`));
};
const getAiLineLabel = (lineId, fallback) => {
  const idKey = lineId != null ? String(lineId) : "";
  const fromMap = idKey ? lineNameById.get(idKey) : null;
  return (fromMap && fromMap.trim())
    ? fromMap.trim()
    : (fallback || (idKey ? `Linea ${idKey}` : "Linea"));
};

const parseWorkPair = (note) => {
  // cerca "work 4/4" dentro note
  const s = String(note || "");
  const m = s.match(/work\s+(\d+)\s*\/\s*(\d+)/i);
  if (!m) return null;
  return { a: Number(m[1]), b: Number(m[2]) };
};

const parseUtilPct = (note) => {
  // cerca "util 100%" dentro note
  const s = String(note || "");
  const m = s.match(/util\s+(\d+)\s*%/i);
  if (!m) return null;
  return Number(m[1]);
};

const overloadNoteIT = (w) => {
  // nota raw (può contenere "picco 1 / capienza 1", ecc.)
  const raw = String(w?.note || "");

  // Se esistono peak/capacity come campi, usiamoli per decidere cosa mostrare
  const peak = Number(w?.peak);
  const cap = Number(w?.capacity);
  const hasPeakCap = Number.isFinite(peak) && Number.isFinite(cap);
  const isRealOverlap = hasPeakCap && peak > cap;

  // Ripulisci eventuali "picco X / capienza Y" dalla nota raw
  const cleaned = raw
    .replace(/•?\s*picco\s*\d+\s*\/\s*capienza\s*\d+/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();

  const util = parseUtilPct(cleaned);
  const work = parseWorkPair(cleaned);

  // Etichetta base (umana)
  const base = isRealOverlap ? "Sovrapposizione" : "Linea satura";

  const parts = [];

  // Se c'è sovrapposizione reale, allora sì: ha senso mostrare picco/capienza
  if (isRealOverlap) parts.push(`picco ${peak} / capienza ${cap}`);

  if (work) parts.push(`giorni ${work.a}/${work.b}`);
  if (Number.isFinite(util)) parts.push(`carico ${util}%`);

  return parts.length ? `${base}: ${parts.join(" • ")}` : base;
};
// =====================================

return (
  <Box p={3}>
   {/* HEADER */}
<Stack spacing={1} mb={3} sx={{ position: "relative", zIndex: 1 }}>
  <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
  {/* Titolo editabile */}
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <TextField
      variant="standard"
      fullWidth
      value={planNameDraft ?? ""}
      onChange={(e) => setPlanNameDraft(e.target.value)}
      onBlur={() => {
        const next = String(planNameDraft || "").trim();
        const cur = String(plan?.name || "").trim();
        if (!next) return;
        if (next === cur) return;
        patchPlanName(next);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") setPlanNameDraft(plan?.name || "");
      }}
      InputProps={{
        disableUnderline: true,
        sx: {
          fontSize: 34,
          fontWeight: 800,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      }}
    />
  </Box>

  {/* Bottone a destra */}
  <Button
    variant="outlined"
    onClick={() => {
      console.log("🟧 CLICK Apri wizard (TimelinePage) id=", id);
      navigate(`/postplans/${id}/wizard`);
    }}
    sx={{ flex: "0 0 auto" }}
  >
    Apri wizard
  </Button>
</Stack>

  <Typography variant="body2" color="text.secondary">
    TX start: {plan.tx_start_date} · Cadenza: {plan.cadence}
  </Typography>
</Stack>
{/* AI ASSISTANT */}
<Card sx={{ mb: 3 }}>
  <CardContent>
    {/* Header riga: titolo + controlli */}
    <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
      <Box>
        <Typography variant="h6">Assistente AI</Typography>
        <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.7 }}>
          {aiMode === "brief"
            ? "Sintesi: cosa monitorare e priorità."
            : aiMode === "overload"
            ? "Sovraccarichi: linee troppo piene (o sovrapposte)."
            : aiMode === "bottlenecks"
            ? "Colli di bottiglia: punti che rallentano la catena."
            : "Rischi: episodi a rischio consegna/TX."}
        </Typography>
      </Box>

      <Stack direction="row" spacing={1} alignItems="center">
        <FormControl size="small">
          <Select value={aiMode} onChange={(e) => setAiMode(e.target.value)}>
            <MenuItem value="brief">Riepilogo</MenuItem>
            <MenuItem value="overload">Sovraccarichi</MenuItem>
            <MenuItem value="bottlenecks">Colli di bottiglia</MenuItem>
            <MenuItem value="risks">Rischi</MenuItem>
          </Select>
        </FormControl>

        <Button variant="contained" onClick={runAI} disabled={aiLoading}>
          {aiLoading ? "Analizzo..." : "Analizza"}
        </Button>

        {aiLoading ? <CircularProgress size={18} /> : null}
      </Stack>
    </Stack>

    {/* Stato loading + elapsed */}
    {aiLoading ? (
      <Typography variant="body2" sx={{ mt: 1.5, opacity: 0.75 }}>
        Analisi in corso… {aiElapsed}s
        {aiElapsed >= 20 ? " (può essere lungo al primo giro)" : ""}
      </Typography>
    ) : null}

    {/* Errore */}
    {aiError ? <Alert severity="error" sx={{ mt: 2 }}>{aiError}</Alert> : null}

    {/* Output */}
    {aiResult ? (
      <Stack spacing={2} sx={{ mt: 2 }}>

        {/* SEZIONE PRINCIPALE (più comprensibile per mode) */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {aiMode === "brief"
              ? "Riepilogo"
              : aiMode === "risks"
              ? "Rischi"
              : aiMode === "bottlenecks"
              ? "Colli di bottiglia"
              : "Sovraccarichi"}
          </Typography>

          <Stack spacing={0.75}>
            {/* BRIEF */}
{aiMode === "brief" && (() => {
  const facts = (Array.isArray(aiResult?.facts) ? aiResult.facts : [])
    .map((f) => (typeof f === "string" ? f : (f?.text || "")))
    .filter(Boolean);

  // Numeri deterministici dal backend (NO regex sui facts)
  const stats =
    aiResult?.context?.derived?.stats ||
    aiResult?.derived?.stats ||
    null;

  const epN = stats?.episodes_count ?? null;
  const opN = stats?.operators_count ?? stats?.people_count ?? null; // finché non rinomini
  const phN = stats?.phase_count ?? null;

  return (
    <Box>
      <Typography variant="body2" sx={{ opacity: 0.85, mb: 1 }}>
        {[
          epN != null ? `Episodi: ${epN}` : null,
          opN != null ? `Operatori: ${opN}` : null,
          phN != null ? `Fasi: ${phN}` : null,
        ].filter(Boolean).join(" • ") || ""}
      </Typography>

      <Stack spacing={0.75}>
        {facts.slice(0, 2).map((t, idx) => (
          <Typography key={idx} variant="body2" sx={{ opacity: 0.9 }}>
            • {t}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
})()}

                {/* RIEPILOGO RISCHI */}
{aiMode === "risks" && Array.isArray(aiResult?.risks) && (() => {
  const criticalCount = aiResult.risks.filter(
    (r) => String(r?.level).toLowerCase() === "high"
  ).length;

  return criticalCount > 0 ? (
    <Typography
      variant="body2"
      sx={{ mb: 1, fontWeight: 600 }}
      color="error"
    >
      ⚠️ Episodi critici: {criticalCount}
    </Typography>
  ) : null;
})()}

            {/* RISKS */}
{aiMode === "risks" && (() => {
  const risks = Array.isArray(aiResult?.risks) ? aiResult.risks : [];
  const criticalCount = risks.filter(r => String(r?.level || "").toLowerCase() === "high").length;

  const fmtRisk = (r) => {
    const ref = Array.isArray(r?.refs) ? r.refs[0] : null;

    const label = ref?.label || r?.label || "Episodio";
    const slack = Number(ref?.slack_days ?? r?.slack_days);

    const tx = ref?.tx_date || r?.tx_date;
    const end = ref?.last_end || r?.last_end;

    const lvl = String(r?.level || "low").toLowerCase();
    const badge = lvl === "high" ? "ALTO" : lvl === "medium" ? "MEDIO" : "BASSO";

    let status = "da verificare";
    if (Number.isFinite(slack)) {
      if (slack < 0) status = `ritardo di ${Math.abs(slack)} gg`;
      else if (slack === 0) status = `a filo TX`;
      else status = `margine ${slack} gg`;
    }

    const detail = (tx || end) ? ` (fine ${fmtDMY(end)} vs TX ${fmtDMY(tx)})` : "";

    return { badge, text: `${label}: ${status}${detail}` };
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Rischi</Typography>

      <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
        ⚠️ Episodi critici: {criticalCount}
      </Typography>

      <Stack spacing={0.75}>
        {risks.slice(0, 6).map((r, idx) => {
          const x = fmtRisk(r);
          return (
            <Typography key={idx} variant="body2" sx={{ opacity: 0.9 }}>
              • <b>{x.badge}</b> — {x.text}
            </Typography>
          );
        })}
      </Stack>
    </Box>
  );
})()}

            {/* BOTTLENECKS */}
{aiMode === "bottlenecks" && (() => {
  const items = Array.isArray(aiResult?.bottlenecks) ? aiResult.bottlenecks : [];

  const parseEvidence = (ev) => {
    const s = String(ev || "");
    const util = (s.match(/util\s*=\s*([0-9]+)/i) || [])[1];
    const win = (s.match(/window\s*=\s*([0-9-]+)\s*->\s*([0-9-]+)/i) || []);
    const start = win[1] ? fmtDateIT(win[1]) : null;
    const end = win[2] ? fmtDateIT(win[2]) : null;

    const parts = [];
    if (util) parts.push(`Carico: ${util}%`);
    if (start && end) parts.push(`Finestra: ${start} → ${end}`);
    return parts.join(" • ");
  };

  return (
    <Box>
      <Stack spacing={0.75}>
        {items.slice(0, 6).map((b, idx) => {
          const lineId = b?.line_id ?? b?.lineId ?? b?.id;
          const label = getAiLineLabel(lineId, b?.label);
          const reason = (b?.reason || "Linea molto carica")
            .replace(/util/gi, "carico")
            .replace(/peak/gi, "picco")
            .replace(/window/gi, "finestra");

          const ev = parseEvidence(b?.evidence);

          return (
            <Typography key={idx} variant="body2" sx={{ opacity: 0.9 }}>
              • <b>{label}</b> — {reason}{ev ? ` • ${ev}` : ""}
            </Typography>
          );
        })}
      </Stack>
    </Box>
  );
})()}

           {/* OVERLOAD (lista sintetica) */}
{aiMode === "overload" && Array.isArray(aiResult?.overload) && aiResult.overload.length > 0 ? (
  <Box>

    <Stack spacing={0.75} sx={{ mb: 1.25 }}>
      {(() => {
        const items = aiResult.overload;
        const hasOverlap = items.some((x) => Number(x?.peak) > Number(x?.capacity));
        const saturatedCount = items.filter((x) => {
          const u = Number(x?.utilization_pct ?? x?.utilizationPct);
          return Number.isFinite(u) ? u >= 95 : true; // fallback: contali comunque
        }).length;

        return (
          <>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
  {hasOverlap
    ? "⚠️ Sovrapposizione rilevata (attività in parallelo oltre la capienza)."
    : "✅ Nessuna sovrapposizione (nessuna attività in parallelo)."}
</Typography>

<Typography variant="body2" sx={{ opacity: 0.85 }}>
  ⚠️ Linee sature (carico ~100%): {saturatedCount} — mostro le principali: {Math.min(saturatedCount, 5)}
</Typography>
          </>
        );
      })()}
    </Stack>

    <Stack spacing={0.5}>
  {aiResult.overload.slice(0, 6).map((w, idx) => {
    const label = getLineTitle(w, idx);
    const start = fmtDateIT(w?.start);
    const end = fmtDateIT(w?.end);

    const peak = Number(w?.peak);
    const cap = Number(w?.capacity);

    // mostra picco/capienza SOLO se c'è sovrapposizione reale
    const showPeakCap =
      Number.isFinite(peak) &&
      Number.isFinite(cap) &&
      peak > cap;

    return (
      <Typography key={idx} variant="body2" sx={{ opacity: 0.9 }}>
        • <b>{label}</b> — {start} → {end}
        {showPeakCap ? ` • picco ${peak} / capienza ${cap}` : ""}
        {overloadNoteIT(w) ? ` — ${overloadNoteIT(w)}` : ""}
      </Typography>
    );
  })}
</Stack>
  </Box>
) : null}

      {/* OVERLOAD (cards) */}
{aiMode === "overload" ? (
  <Box>
    <Typography variant="subtitle1" sx={{ mb: 1 }}>
      Dettaglio linee
    </Typography>

    {Array.isArray(aiResult?.overload) && aiResult.overload.length > 0 ? (
      <Stack spacing={1}>
        {aiResult.overload.slice(0, 10).map((w, idx) => {
          const label = getLineTitle(w, idx);
          const start = fmtDateIT(w?.start);
          const end = fmtDateIT(w?.end);
          const peak = (w?.peak ?? "?");
          const cap = (w?.capacity ?? "?");

          return (
            <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography fontWeight={700}>{label}</Typography>
                <Chip size="small" label={`Picco ${peak} / Capienza ${cap}`} />
              </Stack>

              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {start} → {end}
              </Typography>

              {overloadNoteIT(w) ? (
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.75 }}>
                  {overloadNoteIT(w)}
                </Typography>
              ) : null}
            </Paper>
          );
        })}
      </Stack>
    ) : (
      <Alert severity="info">
        Nessun sovraccarico rilevato (oppure non ci sono sovrapposizioni su giorni lavorativi).
      </Alert>
    )}
  </Box>
) : null}
          </Stack>
        </Box>

        {/* SUGGERIMENTI */}
        {Array.isArray(aiResult.suggestions) && aiResult.suggestions.length > 0 ? (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Suggerimenti
            </Typography>

            <Stack spacing={1}>
              {aiResult.suggestions.slice(0, 3).map((s, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography fontWeight={700}>
                    {s?.title || `Suggerimento ${idx + 1}`}
                  </Typography>

                  {s?.why ? (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {s.why}
                    </Typography>
                  ) : null}

                  {Array.isArray(s?.tradeoffs) && s.tradeoffs.length > 0 ? (
                    <Typography variant="caption" sx={{ display: "block", mt: 1, opacity: 0.75 }}>
                      Trade-off: {s.tradeoffs.join(" • ")}
                    </Typography>
                  ) : null}
                </Paper>
              ))}
            </Stack>
          </Box>
        ) : null}

        {/* DATI MANCANTI */}
        {Array.isArray(aiResult.missing_data) && aiResult.missing_data.length > 0 ? (
          <Alert severity="warning">
            Dati mancanti: {aiResult.missing_data.join(" • ")}
          </Alert>
        ) : null}

      </Stack>
    ) : (
      <Typography variant="body2" sx={{ mt: 2, opacity: 0.7 }}>
        Seleziona una modalità e premi “Analizza”.
      </Typography>
    )}
  </CardContent>
</Card>
    {/* TIMELINE (new) */}
    {layout && Array.isArray(blocksFlat) ? (
      <Box mb={4}>
        <TimelineBars data={data} onSaved={refreshTimeline} />
      </Box>
    ) : null}

    {/* EPISODES (collapsible) */}
<Box>
  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
    <Typography variant="h6">Episodi</Typography>
    <FormControlLabel
  control={
    <Checkbox
      checked={applyToWholePlan}
      onChange={(e) => setApplyToWholePlan(e.target.checked)}
    />
  }
  label="Applica a tutto il piano"
/>
    <FormControlLabel
      control={
        <Switch
          checked={showCompleted}
          onChange={(e) => setShowCompleted(e.target.checked)}
        />
      }
      label="Mostra completati"
    />
  </Stack>

  <Stack spacing={1}>
    {episodes
      .filter((ep) => showCompleted || !isCompleted(ep.id))
      .map((ep) => (
        <Accordion
          key={ep.id}
          expanded={expandedEp === ep.id}
          onChange={() =>
            setExpandedEp(expandedEp === ep.id ? null : ep.id)
          }
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: "100%" }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="subtitle1">
                  Ep.{String(ep.episodeNo).padStart(2, "0")}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  TX: {ep.txDate}
                  </Typography>
                  <Chip
                    size="small"
                    label={ep.metrics?.risk ?? "n/a"}
                    color={
                      ep.metrics?.risk === "green"
                        ? "success"
                        : ep.metrics?.risk === "yellow"
                        ? "warning"
                        : ep.metrics?.risk === "orange"
                        ? "warning"
                        : ep.metrics?.risk === "red"
                        ? "error"
                        : "default"
                    }
                  />
                <Typography variant="body2" color="text.secondary">
                margine: {ep.metrics?.marginWorkingDays ?? "-"} gg
              </Typography>
              </Stack>

              <FormControlLabel
                onClick={(e) => e.stopPropagation()}
                control={
                  <Checkbox
                    checked={isCompleted(ep.id)}
                    onChange={() => toggleCompleted(ep.id)}
                  />
                }
                label="Completato"
              />
            </Stack>
          </AccordionSummary>

          <AccordionDetails>
  <Table size="small">
  <TableHead>
    <TableRow>
      <TableCell sx={{ width: 220 }}>Fase</TableCell>
      <TableCell sx={{ width: 220 }}>Linea</TableCell>
      <TableCell sx={{ width: 320 }}>Parte</TableCell>
      <TableCell sx={{ width: 120 }}>Inizio</TableCell>
      <TableCell sx={{ width: 120 }}>Fine</TableCell>
      <TableCell sx={{ width: 90 }} align="right">Durata</TableCell>
      <TableCell sx={{ width: 90 }} align="right">Margine</TableCell>
      <TableCell sx={{ width: 120 }}>Weekend</TableCell>
      <TableCell sx={{ width: 60 }} align="right">Lock</TableCell>
    </TableRow>
  </TableHead>

  <TableBody>
  {ep.phases.flatMap((ph) =>
    ph.lines.flatMap((ln) => {
      const planId = Number(id);
      const baseLabel = (ln.lineLabel || ln.label || "").trim();
      const fallback = `Linea ${ln.lineNo}`;
      console.log("LINE (after timeline GET)", ln);
      return (ln.blocks || []).map((b) => {
        const cur = Number(b?.durWork);
        const planned = Number(b?.plannedDurWork);
        const hasCur = Number.isFinite(cur) && cur > 0;
        const hasPlanned = Number.isFinite(planned) && planned > 0;
        const delta = hasCur && hasPlanned ? cur - planned : null;

        return (
          <TableRow key={b.id} hover>
            <TableCell>{ph.name}</TableCell>

            {/* LINEA (label di produzione) */}
            <TableCell sx={{ minWidth: 220 }}>
              <TextField
                variant="standard"
                fullWidth
                value={lineDraft[ln.lineId] ?? baseLabel}
                placeholder={fallback}
                onChange={(e) => {
                  const v = e.target.value;
                  setLineDraft((prev) => ({ ...prev, [ln.lineId]: v }));
                }}
                onBlur={(e) => {
                  const newLabel = e.target.value.trim();
                  if (!newLabel) return;
                  if (newLabel === baseLabel) return;

                  if (applyToWholePlan) {
                    // 🔁 BATCH: tutte le linee (fase + lineNo)
                    patchLineLabelBatch(
                      Number(id),
                      ph.phaseId,
                      ln.lineNo,
                      newLabel
                    );
                  } else {
                    // 🎯 SINGLE: solo questa linea
                    const lineId = ln.lineId;
                    if (!lineId) return;
                    patchLineLabel(Number(id), lineId, newLabel);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
              />
            </TableCell>

            {/* PARTE (nota del blocco) */}
            <TableCell>
              <TextField
                size="small"
                fullWidth
                placeholder="Parte di puntata…"
                defaultValue={b.partLabel ?? ""}
                onBlur={(e) => patchBlockNote(b.id, e.target.value)}
              />
            </TableCell>

            <TableCell>{b.startDate}</TableCell>
            <TableCell>{b.endDate}</TableCell>

            <TableCell align="right">{hasCur ? cur : ""}</TableCell>

            <TableCell align="right">
              {delta == null ? "" : (
                <Tooltip
                  title={
                    delta > 0
                      ? `${delta} gg oltre il piano`
                      : delta < 0
                      ? `${Math.abs(delta)} gg di anticipo`
                      : `In linea con il piano`
                  }
                >
                  <Chip
                    size="small"
                    label={delta > 0 ? `+${delta}` : `${delta}`}
                    color={delta > 0 ? "error" : delta < 0 ? "success" : "default"}
                    variant={delta === 0 ? "outlined" : "filled"}
                  />
                </Tooltip>
              )}
            </TableCell>

            <TableCell>
              <Stack direction="row" spacing={1} alignItems="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!b.allowSat}
                      onChange={(e) =>
                        patchBlockOverride(b.id, e.target.checked, !!b.allowSun)
                      }
                    />
                  }
                  label="Sab"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!b.allowSun}
                      onChange={(e) =>
                        patchBlockOverride(b.id, !!b.allowSat, e.target.checked)
                      }
                    />
                  }
                  label="Dom"
                />
              </Stack>
            </TableCell>

            <TableCell align="right">
              {lockByBlockId.get(b.id) ? "🔒" : ""}
            </TableCell>
          </TableRow>
        );
      });
    })
  )}
</TableBody>
</Table>
</AccordionDetails>

        </Accordion>
      ))}
  </Stack>
</Box>
    </Box>
  );
}